import React, { useEffect, useMemo, useRef, useState, useTransition } from 'react';

import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';

import { Alert, Box, Container, Paper, Tab, Tabs } from '@mui/material';

import { useGetProperty } from '~/hooks/apis/accommodation/useGetProperty';
import { useGetPackageInclusionsByOffer } from '~/hooks/apis/inclusions/useGetPackageInclusionsByOffer';
import { useGetOffer } from '~/hooks/apis/offers/useGetOffer';

import { isLTLEHotelOfferType } from '~/utils/offer';

import OfferBuilderSidebar from '../OfferBuilderSidebar';

import DefaultContent from './DefaultContent';
import OfferBuilderImagesVideo from './ImagesVideo';
import Inclusions from './Inclusions';
import { type MasterInclusion, buildMasterInclusionsFromOffer } from './Inclusions/utils';
import LazyContentTabPanel from './LazyContentTabPanel';
import OfferPackages from './OfferPackages';
import PropertyInformation, { type OfferBuilderProperty, formatPropertyLocation } from './PropertyInformation';
import OfferBuilderRooms from './Rooms';
import useOfferBuilderMedia from './useOfferBuilderMedia';

const DEFAULT_CONTENT_TAB = 'Default content';
const IMAGES_VIDEO_TAB = 'Images & Video';
const INCLUSIONS_TAB = 'Inclusions';
const OFFER_PACKAGES_TAB = 'Offer packages';
const PROPERTY_INFORMATION_TAB = 'Property information';
const ROOMS_TAB = 'Rooms';

const CONTENT_TABS = [
  PROPERTY_INFORMATION_TAB,
  ROOMS_TAB,
  OFFER_PACKAGES_TAB,
  INCLUSIONS_TAB,
  DEFAULT_CONTENT_TAB,
  IMAGES_VIDEO_TAB,
];

export default function OfferBuilderContentPage() {
  const { id_offer: offerId } = useParams<{ id_offer: string }>();
  const [selectedTab, setSelectedTab] = useState(DEFAULT_CONTENT_TAB);
  const [activeContentTab, setActiveContentTab] = useState(DEFAULT_CONTENT_TAB);
  const [draftMasterInclusions, setDraftMasterInclusions] = useState<Array<MasterInclusion>>([]);
  const [savedMasterInclusions, setSavedMasterInclusions] = useState<Array<MasterInclusion>>([]);
  const hasUserEditedInclusionsRef = useRef(false);
  const initialisedInclusionsOfferIdRef = useRef<string | undefined>(undefined);
  const [, startTabChangeTransition] = useTransition();

  const { data: offer, isLoading: isLoadingOffer, error: offerError } = useGetOffer(offerId);
  const isSupportedOfferType = !!offer?.type && isLTLEHotelOfferType(offer.type);
  const hasOfferLoadError = !isLoadingOffer && !!offerError && !offer;
  const shouldRenderContentTabs = isLoadingOffer || (!!offer && isSupportedOfferType);
  const propertyId = isSupportedOfferType
    ? offer?.packages?.find((offerPackage) => offerPackage?.fk_property_id)?.fk_property_id
    : undefined;
  const vendorId = offer?.vendor_account_id;
  const { data: packageInclusions, isLoading: isLoadingInclusions } = useGetPackageInclusionsByOffer({
    offerId,
    enabled: shouldRenderContentTabs,
  });
  const offerInclusions = useMemo(() => buildMasterInclusionsFromOffer(packageInclusions), [packageInclusions]);

  const {
    data: property,
    isLoading: isLoadingProperty,
    error: propertyError,
  } = useGetProperty<OfferBuilderProperty>({
    propertyId,
    enabled: isSupportedOfferType,
  });

  const hotelName = property?.name || propertyId || 'Property';
  const propertyLocation = formatPropertyLocation(property);
  const media = useOfferBuilderMedia({
    offerId,
    images: offer?.images,
    propertyRoomTypes: property?.room_types,
    videos: offer?.videos,
  });

  const showContentTab = (tab: string) => {
    setSelectedTab(tab);
    startTabChangeTransition(() => setActiveContentTab(tab));
  };

  useEffect(() => {
    if (isLoadingInclusions) {
      return;
    }

    if (initialisedInclusionsOfferIdRef.current !== offerId) {
      initialisedInclusionsOfferIdRef.current = offerId;
      hasUserEditedInclusionsRef.current = false;
      setDraftMasterInclusions(offerInclusions);
      setSavedMasterInclusions(offerInclusions);
      return;
    }

    setSavedMasterInclusions(offerInclusions);

    if (!hasUserEditedInclusionsRef.current) {
      setDraftMasterInclusions(offerInclusions);
    }
  }, [isLoadingInclusions, offerId, offerInclusions]);

  const handleMasterInclusionsChange: React.Dispatch<React.SetStateAction<Array<MasterInclusion>>> = (nextValue) => {
    hasUserEditedInclusionsRef.current = true;
    setDraftMasterInclusions(nextValue);
  };

  const handleMasterInclusionsSaved = (nextMasterInclusions: Array<MasterInclusion>) => {
    hasUserEditedInclusionsRef.current = false;
    setDraftMasterInclusions(nextMasterInclusions);
    setSavedMasterInclusions(nextMasterInclusions);
  };

  return (
    <Container maxWidth="xl">
      <Helmet>
        <title>Offer Builder | {hotelName}</title>
      </Helmet>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '280px minmax(0, 1fr)' } }}>
        <OfferBuilderSidebar
          hotelCode={property?.hotel_code}
          hotelName={hotelName}
          location={propertyLocation}
          offerId={offerId}
        />

        <Box sx={{ px: { xs: 2, md: 4 }, py: 3, pb: 14 }}>
          {hasOfferLoadError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              Unable to load offer details.
            </Alert>
          )}

          {!isLoadingOffer && offer && !isSupportedOfferType && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              Offer Builder is only available for flash offers.
            </Alert>
          )}

          {shouldRenderContentTabs && (
            <>
              <Paper sx={{ position: 'sticky', top: 0, zIndex: 2, mb: 3 }}>
                <Tabs
                  value={selectedTab}
                  onChange={(_event, value) => showContentTab(value)}
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  {CONTENT_TABS.map((tab) => (
                    <Tab key={tab} value={tab} label={tab} />
                  ))}
                </Tabs>
              </Paper>

              <LazyContentTabPanel activeTab={activeContentTab} tab={PROPERTY_INFORMATION_TAB}>
                <PropertyInformation
                  property={property}
                  vendorId={vendorId}
                  isLoadingProperty={isLoadingOffer || isLoadingProperty}
                  offerError={offerError}
                  propertyError={propertyError}
                />
              </LazyContentTabPanel>
              <LazyContentTabPanel activeTab={activeContentTab} tab={ROOMS_TAB}>
                <OfferBuilderRooms
                  media={media}
                  offerPackages={offer?.packages}
                  isLoadingProperty={isLoadingOffer || isLoadingProperty}
                  onOpenImagesVideo={() => showContentTab(IMAGES_VIDEO_TAB)}
                  propertyError={propertyError}
                  propertyId={propertyId}
                  propertyName={hotelName}
                  propertyRoomTypes={property?.room_types}
                  vendorId={vendorId}
                />
              </LazyContentTabPanel>
              <LazyContentTabPanel activeTab={activeContentTab} tab={OFFER_PACKAGES_TAB}>
                <OfferPackages
                  offerId={offerId}
                  masterInclusions={isLoadingInclusions ? undefined : savedMasterInclusions}
                  packageInclusions={packageInclusions}
                  packages={offer?.packages}
                  property={property}
                  propertyRoomTypes={property?.room_types}
                  vendorId={vendorId}
                  offerType={offer?.type}
                  isLoadingOffer={isLoadingOffer}
                  offerError={offerError}
                  onOpenInclusions={() => showContentTab(INCLUSIONS_TAB)}
                />
              </LazyContentTabPanel>
              <LazyContentTabPanel activeTab={activeContentTab} tab={INCLUSIONS_TAB}>
                <Inclusions
                  inclusions={draftMasterInclusions}
                  isLoadingInclusions={isLoadingInclusions}
                  offerId={offerId}
                  packageInclusions={packageInclusions}
                  packages={offer?.packages}
                  vendorId={vendorId}
                  onChange={handleMasterInclusionsChange}
                  onSaved={handleMasterInclusionsSaved}
                />
              </LazyContentTabPanel>
              <LazyContentTabPanel activeTab={activeContentTab} tab={DEFAULT_CONTENT_TAB}>
                <DefaultContent
                  offerAdditionalDescription={offer?.additional_description}
                  offerDescription={offer?.description}
                  offerFacilities={offer?.facilities}
                  offerFinePrint={offer?.fine_print}
                  offerGettingThere={offer?.getting_there}
                  offerHighlights={offer?.highlights}
                  offerName={offer?.name}
                  offerPreheader={offer?.preheader}
                  offerSaleUnit={offer?.sale_unit}
                  offerSlug={offer?.slug}
                  offerStatus={offer?.status}
                  offerSubjectLine={offer?.subject_line}
                  offerType={offer?.type}
                  propertyId={propertyId}
                />
              </LazyContentTabPanel>
              <LazyContentTabPanel activeTab={activeContentTab} tab={IMAGES_VIDEO_TAB}>
                <OfferBuilderImagesVideo
                  media={media}
                  offerId={offerId}
                  offerType={offer?.type}
                  propertyId={property?.id}
                  propertyLogoId={property?.logo_id_cloudinary_external}
                  propertyRoomTypes={property?.room_types}
                />
              </LazyContentTabPanel>
            </>
          )}
        </Box>
      </Box>
    </Container>
  );
}
